import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import Headline from 'uiKit/texts/Headline'
import Search from 'uiKit/Search/Search'
import Table from 'uiKit/table/Table'
import TableRow from 'uiKit/table/TableRow'
import TableCell from 'uiKit/table/TableCell'
import Pagination from 'uiKit/table/Pagination'
import Select from 'react-select'
import DateControl from 'uiKit/DateControlNew'
import BreadCrumbs from 'uiKit/BreadCrumbs/BreadCrumbs'
import LoaderScreen from 'uiKit/loaders/loaderScreen'
import LoaderOverlay from 'uiKit/loaders/loaderOverlay'
import NoChartData from 'uiKit/NoChartData'
import * as S from '../../Dashboard.style'
import {
  Container as TotalUsersContainer,
  FiltersRow,
  RolesSelectWrap,
  StatusSelectWrap,
  ManagerSelectWrap,
  PaginationSpacer,
} from '../TotalUsers/TotalUsers.style'
import { loadManagersList, loadTotalChatList, exportTotalChatCsv } from '../../api/dashboard'
import { DateControlContext } from 'contexts/DateControlContext'
import { getDashboardDateApiParams, shouldShowAccountStatusForDateRange } from '../../helpers/dashboardDateParams'
import { useScrollToTop } from 'hooks/useScrollToTop'
import { canViewCarecoDashboard } from '../../../../security'
import { isQcsBotPayload } from '../../../../constants/qcs'
import { formatAccountStatus } from '../TotalUsers/AccountStatusFilter'
import { InactiveSelectOption, InactiveTagOption, mapManagerSelectOptions } from '../InactiveTagSelectOption'

type ChatRow = {
  id: number
  firstName: string
  lastName: string
  role: string
  manager: string
  active: string
  accountStatus: string
  totalQueries: number | string
  frequencyDays: number | string
}

const baseColumnTitles = [
  { title: 'First name', maxWidth: 180, marginRight: 10 },
  { title: 'Last name', maxWidth: 180, marginRight: 10 },
  { title: 'Role', maxWidth: 140 },
  { title: 'Manager', maxWidth: 220, marginRight: 10 },
  { title: 'Active chat user', maxWidth: 160 },
  { title: 'Total queries', maxWidth: 140 },
  { title: 'Frequency (days)', maxWidth: 160 },
  {
    title: 'Account status',
    maxWidth: 160,
    tooltipPlace: 'left',
    tooltipText:
      'Displays the current account status of each user, showing active users and inactive users who have been deleted.',
  },
]

const searchInputStyle = {
  borderRadius: 8,
  width: '327px',
  height: '45px',
  padding: '14px 44px 19px 24px',
  minWidth: 240,
  flex: '0 1 327px',
  border: '1px solid rgba(226, 232, 234, 1)',
}

type Props = RouteComponentProps & { activeBot?: any, adminUser?: any }

export const TotalChats: React.FC<Props> = ({ history, match, activeBot, adminUser }) => {
  useScrollToTop()

  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [page, setPage] = useState(0)
  const [activeChatUser, setActiveChatUser] = useState<{ label: string; value: string } | null>(null)
  const [manager, setManager] = useState<{ label: string; value: string } | null>(null)
  const [role, setRole] = useState<{ label: string; value: string } | null>(null)
  const { botId: routeBotId } = (match as any)?.params || {}
  const botId = routeBotId || activeBot?.id
  const isCarecoAdmin = useMemo(
    () => canViewCarecoDashboard(botId, adminUser?.botAccesses),
    [botId, adminUser],
  )
  const hideFrequency = isQcsBotPayload(activeBot)
  const [rows, setRows] = useState<ChatRow[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [isExporting, setIsExporting] = useState(false)
  const {
    dateRange: { startDate, endDate },
    frequency,
  } = useContext(DateControlContext)
  const showAccountStatus = shouldShowAccountStatusForDateRange(isCarecoAdmin, frequency)
  const columnTitles = showAccountStatus
    ? baseColumnTitles
    : baseColumnTitles.filter(column => column.title !== 'Account status')
  const prevDatesRef = useRef({ start: startDate?.valueOf?.(), end: endDate?.valueOf?.() })
  const lastFetchKeyRef = useRef<string | null>(null)

  const activeChatUserOptions = [
    { label: 'Active chat user', value: '' },
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' },
  ]

  const roleOptions = [
    { label: 'All roles', value: '' },
    { label: 'Carer', value: '1' },
    { label: 'Manager', value: '2' },
  ]

  const [managerOptions, setManagerOptions] = useState<InactiveSelectOption[]>([
    { label: 'All Managers', value: '', isInactive: false },
  ])
  const [isLoadingManagers, setIsLoadingManagers] = useState(false)

  const loadManagersOnOpen = () => {
    if (!botId || isLoadingManagers) return

    setIsLoadingManagers(true)
    loadManagersList(botId)
      .then((res: any[]) => {
        if (Array.isArray(res)) {
          setManagerOptions(mapManagerSelectOptions(res))
        }
      })
      .finally(() => setIsLoadingManagers(false))
  }

  useEffect(() => {
    setManagerOptions([{ label: 'All Managers', value: '', isInactive: false }])
    setManager(null)
    setPage(0)
    lastFetchKeyRef.current = null
  }, [botId])

  const buildChatRequestParams = (pageOverride = page) => {
    const params: any = {
      page: pageOverride + 1,
      size: 10,
      ...getDashboardDateApiParams(frequency, startDate, endDate, isCarecoAdmin),
    }
    if (debouncedQuery) params.search = debouncedQuery
    if (manager?.value) params.managerId = manager.value
    if (activeChatUser?.value) params.status = activeChatUser.value
    if (role?.value) params.roleId = role.value
    return params
  }

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedQuery(query)
      setPage(0)
    }, 500)
    return () => clearTimeout(handle)
  }, [query])

  useEffect(() => {
    if (!botId) return

    const start = startDate?.valueOf?.()
    const end = endDate?.valueOf?.()
    const datesChanged = prevDatesRef.current.start !== start || prevDatesRef.current.end !== end
    const effectivePage = datesChanged ? 0 : page

    if (datesChanged) {
      prevDatesRef.current = { start, end }
      if (page !== 0) {
        setPage(0)
      }
    }

    const fetchKey = [
      botId,
      effectivePage,
      frequency,
      start,
      end,
      debouncedQuery,
      manager?.value || '',
      activeChatUser?.value || '',
      role?.value || '',
    ].join('|')

    if (lastFetchKeyRef.current === fetchKey) return
    lastFetchKeyRef.current = fetchKey

    setLoading(true)

    loadTotalChatList(botId, buildChatRequestParams(effectivePage))
      .then((res: any) => {
        if (res && Array.isArray(res.content)) {
          const mapped: ChatRow[] = res.content.map((u: any) => ({
            id: u.userId,
            firstName: u.firstName,
            lastName: u.lastName,
            role: u.role,
            manager: u.managerName || '-',
            active: u.activeChatUser,
            accountStatus: u.userStatus || u.accountStatus || u.status,
            totalQueries: u.totalQueries,
            frequencyDays: u.frequencyDays,
          }))
          setRows(mapped)
          setTotalCount(Number(res.totalElements) || mapped.length)
        } else {
          setRows([])
          setTotalCount(0)
        }
      })
      .catch(() => {
        setRows([])
        setTotalCount(0)
      })
      .finally(() => setLoading(false))
  }, [botId, page, startDate, endDate, frequency, debouncedQuery, manager, activeChatUser, role])

  const selectStyles = {
    control: (base: any) => ({
      ...base,
      minHeight: 45,
      height: 45,
      borderRadius: 10,
      borderColor: 'var(--color-input-stroke)',
      '&:hover': { borderColor: 'var(--color-input-stroke)' },
    }),
    valueContainer: (base: any) => ({ ...base, height: 45, padding: '0 12px', fontSize: 14, fontWeight: 500 }),
    indicatorsContainer: (base: any) => ({ ...base, height: 45 }),
    menu: (base: any) => ({ ...base, zIndex: 5 }),
    option: (base, state) => ({
      ...base,
      whiteSpace: 'normal',
      wordWrap: 'break-word',
      overflow: 'hidden',
      backgroundColor: state.isSelected ? '#E8F4FC' : base.backgroundColor,
      color: state.isSelected ? '#111827' : base.color,
      cursor: 'pointer',
      '&:active': {
        backgroundColor: '#E8F4FC',
      },
      '&:hover': {
        backgroundColor: state.isSelected ? '#E8F4FC' : '#F3F4F6',
      },
    }),
    indicatorSeparator: () => ({ display: 'none' }),
  }

  const shouldCloseOnScroll = (event: any) => {
    const target = (event && (event.target as HTMLElement)) || null
    if (!target) return true
    const withinMenu =
      target.closest('.chats-select__menu')
      || target.closest('.chats-select__menu-list')
      || target.closest('.account-status-select__menu')
      || target.closest('.account-status-select__menu-list')
    return !withinMenu
  }

  const handleDashboardClick = () => {
    const currentUrl = (match as any)?.url || ''
    const dashboardBase = currentUrl.replace(/\/chats$/, '') || '/dashboard'
    history.push(dashboardBase)
  }

  const handleExport = async () => {
    if (!botId) return
    try {
      setIsExporting(true)
      const blob = await exportTotalChatCsv(botId, buildChatRequestParams())

      if (!blob) return
      const url = window.URL.createObjectURL(blob as any)
      const a = document.createElement('a')
      a.href = url
      a.download = 'total-chats.xlsx'
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (e) {
      // Error feedback handled in API
    } finally {
      setIsExporting(false)
    }
  }

  const renderBody = () => {
    if (loading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <LoaderScreen size={{ circleSize: 60, border: 8 }} />
        </div>
      )
    }

    if (rows.length === 0) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 0' }}>
          <NoChartData text={'No chats found'} isUserData={true} />
        </div>
      )
    }

    return (
      <>
        <Table titles={columnTitles}>
          {rows.map((row, idx) => (
            <TableRow key={row.id} style={{ background: idx % 2 ? 'var(--color-table-row-primary)' : undefined }}>
              <TableCell style={{ maxWidth: columnTitles[0].maxWidth, marginRight: 10 }}>{row.firstName}</TableCell>
              <TableCell style={{ maxWidth: columnTitles[1].maxWidth, marginRight: 10 }}>{row.lastName}</TableCell>
              <TableCell style={{ maxWidth: columnTitles[2].maxWidth }}>
                {row.role === 'SUPERVISOR'
                  ? 'Manager'
                  : row.role
                    ? row.role.replace('_', ' ').charAt(0).toUpperCase() + row.role.replace('_', ' ').slice(1).toLowerCase()
                    : '-'}
              </TableCell>
              <TableCell style={{ maxWidth: columnTitles[3].maxWidth, marginRight: 10 }}>{row.manager}</TableCell>
              <TableCell
                style={{
                  maxWidth: columnTitles[4].maxWidth,
                  color: row.active === 'Yes' ? 'var(--color-text-success)' : 'rgb(255, 145, 0)',
                  fontWeight: 500,
                }}>
                {row.active}
              </TableCell>
              <TableCell style={{ maxWidth: columnTitles[5].maxWidth }}>{row.totalQueries}</TableCell>
              <TableCell style={{ maxWidth: columnTitles[6].maxWidth }}>{row.frequencyDays}</TableCell>
              {showAccountStatus && (
                <TableCell style={{ maxWidth: columnTitles[7].maxWidth }}>
                  {formatAccountStatus(row.accountStatus)}
                </TableCell>
              )}
            </TableRow>
          ))}
        </Table>

        <PaginationSpacer>
          <Pagination
            page={page}
            rowsPerPage={10}
            count={totalCount}
            onChangePage={(_, nextPage) => setPage(nextPage)}
          />
        </PaginationSpacer>
      </>
    )
  }

  return (
    <TotalUsersContainer>
      {isExporting && <LoaderOverlay />}
      <PaginationSpacer mt={30}>
        <BreadCrumbs items={[{ label: 'Dashboard', onClick: handleDashboardClick }, { label: 'Chats' }]} />
      </PaginationSpacer>
      <S.HeadlineWrap>
        <Headline title={`Chats (${totalCount})`} />
        <DateControl
          isCarecoAdmin={isCarecoAdmin}
          handleExport={isCarecoAdmin ? handleExport : undefined}
          hideFrequency={hideFrequency}
        />
      </S.HeadlineWrap>
      <FiltersRow>
        <Search
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search by name"
          inputStyle={searchInputStyle}
        />
        <RolesSelectWrap>
          <Select
            styles={selectStyles}
            options={roleOptions}
            value={role || roleOptions[0]}
            onChange={opt => {
              setRole(opt as any)
              setPage(0)
            }}
            isSearchable={false}
            closeMenuOnScroll={shouldCloseOnScroll}
            classNamePrefix="chats-select"
            menuPosition={'absolute'}
          />
        </RolesSelectWrap>
        <StatusSelectWrap>
          <Select
            styles={selectStyles}
            options={activeChatUserOptions}
            value={activeChatUser || activeChatUserOptions[0]}
            onChange={opt => {
              setActiveChatUser(opt as any)
              setPage(0)
            }}
            isSearchable={false}
            closeMenuOnScroll={shouldCloseOnScroll}
            classNamePrefix="chats-select"
            menuPosition={'absolute'}
          />
        </StatusSelectWrap>
        <ManagerSelectWrap>
          <Select
            styles={selectStyles}
            options={managerOptions}
            value={manager || managerOptions[0]}
            onChange={opt => {
              setManager(opt as any)
              setPage(0)
            }}
            onMenuOpen={loadManagersOnOpen}
            isLoading={isLoadingManagers}
            isSearchable={false}
            closeMenuOnScroll={shouldCloseOnScroll}
            classNamePrefix="chats-select"
            menuPosition={'absolute'}
            components={{ Option: InactiveTagOption }}
          />
        </ManagerSelectWrap>
      </FiltersRow>

      {renderBody()}
    </TotalUsersContainer>
  )
}

const mapStateToProps = (state: any) => ({
  activeBot: state.activeBot,
  adminUser: state.adminUser,
})

export default withRouter(connect(mapStateToProps)(TotalChats))
